import { createFileRoute } from '@tanstack/react-router';
import { CalendarClock, Pause, Play, Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useSurveySchedules,
  usePauseSurveySchedule,
  useResumeSurveySchedule,
} from '@/features/surveys/api/use-survey-schedules';
import { CreateScheduleDialog } from '@/features/surveys/components/CreateScheduleDialog';
import type { SurveyScheduleListItem } from '@/features/surveys/types';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/surveys/schedules/')({
  component: ScheduleListPage,
});

function ScheduleListPage() {
  const { t } = useTranslation('surveys');
  const [dialogOpen, setDialogOpen] = useState(false);
  const { data, isLoading } = useSurveySchedules({});
  const pauseMutation = usePauseSurveySchedule();
  const resumeMutation = useResumeSurveySchedule();

  const FREQUENCY_LABELS: Record<string, string> = {
    monthly: t('schedules.frequencyMonthly'),
    quarterly: t('schedules.frequencyQuarterly'),
    semi_annually: t('schedules.frequencySemiAnnually'),
    annually: t('schedules.frequencyAnnually'),
    custom: t('schedules.frequencyCustom'),
  };

  const handleToggleActive = async (schedule: SurveyScheduleListItem) => {
    try {
      if (schedule.isActive) {
        await pauseMutation.mutateAsync(schedule.id);
        toast.success(t('toast.schedulePaused2'));
      } else {
        await resumeMutation.mutateAsync(schedule.id);
        toast.success(t('toast.scheduleResumed2'));
      }
    } catch {
      toast.error(t('toast.failedToUpdateSchedule'));
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('schedules.title')}</h1>
          <p className="text-muted-foreground">{t('schedules.description')}</p>
        </div>
        <Button onClick={() => { setDialogOpen(true); }}>
          <Plus className="mr-2 h-4 w-4" />
          {t('schedules.newSchedule')}
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : !data?.items.length ? (
        <EmptyState
          icon={CalendarClock}
          title={t('schedules.emptyTitle')}
          description={t('schedules.emptyDescription')}
        >
          <Button onClick={() => { setDialogOpen(true); }} className="mt-4">
            <Plus className="mr-2 h-4 w-4" />
            {t('schedules.createSchedule')}
          </Button>
        </EmptyState>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('schedules.name')}</TableHead>
                  <TableHead>{t('schedules.template')}</TableHead>
                  <TableHead>{t('schedules.frequency')}</TableHead>
                  <TableHead>{t('schedules.deadlineDays')}</TableHead>
                  <TableHead>{t('schedules.status')}</TableHead>
                  <TableHead>{t('schedules.lastRun')}</TableHead>
                  <TableHead>{t('schedules.nextRun')}</TableHead>
                  <TableHead>{t('schedules.runCount')}</TableHead>
                  <TableHead className="w-[80px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((s: SurveyScheduleListItem) => (
                  <TableRow key={s.id} data-testid={`schedule-row-${s.id}`}>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell>{s.templateName}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {FREQUENCY_LABELS[s.frequency] ?? s.frequency}
                      </Badge>
                    </TableCell>
                    <TableCell>{s.deadlineDays}</TableCell>
                    <TableCell>
                      <Badge variant={s.isActive ? 'default' : 'secondary'}>
                        {s.isActive ? t('schedules.active') : t('schedules.paused')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {s.latestRun ? (
                        <div className="space-y-1 text-sm">
                          <Badge
                            variant={s.latestRun.status === 'failed' ? 'destructive' : 'outline'}
                          >
                            {s.latestRun.status}
                          </Badge>
                          <p className="text-muted-foreground">
                            {new Date(s.latestRun.scheduledFor).toLocaleString()}
                          </p>
                          {s.latestRun.errorMessage && (
                            <p className="max-w-xs text-xs text-destructive">
                              {s.latestRun.errorMessage}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">{t('schedules.noRunsYet')}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {s.nextRunAt
                        ? new Date(s.nextRunAt).toLocaleDateString()
                        : '-'}
                    </TableCell>
                    <TableCell>{s.runCount}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => void handleToggleActive(s)}
                        disabled={pauseMutation.isPending || resumeMutation.isPending}
                        title={s.isActive ? t('schedules.pauseSchedule') : t('schedules.resumeSchedule')}
                      >
                        {s.isActive ? (
                          <Pause className="h-4 w-4" />
                        ) : (
                          <Play className="h-4 w-4" />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <CreateScheduleDialog
        isOpen={dialogOpen}
        onClose={() => { setDialogOpen(false); }}
      />
    </div>
  );
}
